import React, {useEffect, useState} from 'react';
import {useAdventureStore, AdventureSlot} from '../../stores/adventureStore';
import Modal from '../common/Modal';

const CARD_WIDTH = 140;
const CARD_HEIGHT = 210; // 2:3 aspect ratio (landscape row layout)
const GAP = 16;

// Cap the card list height in portrait so the intro panel and buttons stay in view.
const PORTRAIT_LIST_MAX_HEIGHT = '40vh';
const INTRO_TEXT_MAX_HEIGHT = 160;

interface Props {
  characterName: string;
  busy?: boolean;
  onConfirm: (adventureId: string) => void;
  onCancel: () => void;
}

/** Tracks whether the viewport is in a portrait (taller-than-wide) aspect ratio. */
function useIsPortrait(): boolean {
  const [isPortrait, setIsPortrait] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight >= window.innerWidth : false,
  );
  useEffect(() => {
    const onResize = () => setIsPortrait(window.innerHeight >= window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPortrait;
}

export const AdventureSelect: React.FC<Props> = ({characterName, busy, onConfirm, onCancel}) => {
  const {slots, selectedAdventureId, isLoading, error, fetchAdventures, selectAdventure} =
    useAdventureStore();
  const isPortrait = useIsPortrait();

  useEffect(() => {
    selectAdventure(null);
    // LobbyScreen pre-fetches adventures on modal open so this component renders immediately.
  }, []);

  const selectedSlot = slots.find((s) => s.id === selectedAdventureId) ?? null;
  const canBegin = !!selectedSlot && !selectedSlot.isComingSoon && !busy;

  const renderCard = (slot: AdventureSlot) => {
    const isComingSoon = slot.isComingSoon;
    const isSelected = slot.id === selectedAdventureId;

    return (
      <button
        key={slot.id}
        onClick={() => !isComingSoon && selectAdventure(slot.id)}
        disabled={busy || isComingSoon}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: CARD_WIDTH,
          minWidth: CARD_WIDTH,
          height: CARD_HEIGHT,
          flexShrink: 0,
          background: isComingSoon ? '#1a1d24' : isSelected ? '#2e3a52' : '#23262e',
          border: '2px solid ' + (
            isSelected
              ? '#6b8cce'
              : isComingSoon
                ? '#2a2d35'
                : '#3a3d45'
          ),
          borderRadius: 8,
          cursor: isComingSoon || busy ? 'default' : 'pointer',
          fontFamily: 'monospace',
          opacity: isComingSoon ? 0.5 : 1,
          transition: 'opacity 0.15s ease, border-color 0.15s ease',
          padding: 0,
          textAlign: 'left',
        }}
      >
        {/* Portrait art area */}
        <div style={{
          flex: '0 0 100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isComingSoon
            ? '#15181e'
            : 'linear-gradient(180deg, #2a3460 0%, #1e2640 100%)',
          borderBottom: '1px solid ' + (isSelected ? '#6b8cce33' : '#3a3d45'),
          fontSize: 32,
          color: isComingSoon ? '#3a3d45' : '#e8e8ff',
        }}>
          {isComingSoon ? '🔒' : (slot as any).icon || '⚔️'}
        </div>

        {/* Content area */}
        <div style={{
          flex: 1,
          minWidth: 0,
          padding: '10px 10px 12px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            fontSize: 13,
            fontWeight: 'bold',
            color: isComingSoon ? '#555' : '#e8e8ff',
            lineHeight: 1.3,
            marginBottom: 4,
            // Never truncate the title — wrap onto as many lines as needed.
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}>
            {slot.title}
          </div>

          <div style={{
            fontSize: 10,
            color: '#8a8',
          }}>
            {isComingSoon ? '???' : `Lv. ${slot.levels.min}–${slot.levels.max}`}
          </div>
        </div>
      </button>
    );
  };

  return (
    <Modal isOpen={true} onClose={busy ? undefined : onCancel}>
      <div style={{
        width: isPortrait ? 400 : 540,
        maxWidth: 'calc(100vw - 48px)',
        fontFamily: 'monospace',
      }}>
        <div style={{fontSize: 16, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 4}}>
          Choose an Adventure
        </div>
        <div style={{fontSize: 11, color: '#666', marginBottom: 20}}>
          For {characterName}
        </div>

        {isLoading && (
          <div style={{color: '#888', fontSize: 13, textAlign: 'center', padding: '24px 0'}}>
            Loading adventures…
          </div>
        )}

        {error && (
          <div style={{color: '#f88', fontSize: 13, marginBottom: 12}}>
            {error}
            <button
              onClick={() => fetchAdventures(1)}
              style={{marginLeft: 12, background: 'none', border: '1px solid #f88', color: '#f88', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12}}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && (
          <div style={{
            display: 'flex',
            flexDirection: isPortrait ? 'column' : 'row',
            gap: GAP,
            ...(isPortrait
              ? {alignItems: 'center', overflowY: 'auto', maxHeight: PORTRAIT_LIST_MAX_HEIGHT, paddingRight: 4}
              // Three cards plus their gaps are narrower than the dialog, which otherwise left-shoves
              // the row and leaves dead space on the right. `safe` matters because this row can also
              // scroll: plain `center` would push the first card past the left edge once there are
              // more adventures than fit, with no way to scroll back to it.
              : {justifyContent: 'safe center', overflowX: 'auto', paddingBottom: 8}),
          }}>
            {slots.map(renderCard)}
          </div>
        )}

        {/* Intro detail panel — shown only once a real adventure is selected. */}
        {!isLoading && !error && selectedSlot && !selectedSlot.isComingSoon && (
          <div style={{
            marginTop: 16,
            background: '#1b1e26',
            border: '1px solid #3a3d45',
            borderRadius: 8,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{fontSize: 13, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 2, overflowWrap: 'break-word'}}>
              {selectedSlot.title}
            </div>
            <div style={{fontSize: 10, color: '#8a8', marginBottom: 8}}>
              Lv. {selectedSlot.levels.min}–{selectedSlot.levels.max}
            </div>
            <div style={{
              fontSize: 12,
              color: '#bcbcc8',
              lineHeight: 1.5,
              overflowY: 'auto',
              maxHeight: INTRO_TEXT_MAX_HEIGHT,
              whiteSpace: 'pre-wrap',
            }}>
              {selectedSlot.intro}
            </div>
          </div>
        )}

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24}}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              background: 'none',
              color: '#aaa',
              border: '1px solid #555',
              borderRadius: 6,
              padding: '8px 16px',
              fontFamily: 'monospace',
              fontSize: 13,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Back
          </button>
          <button
            onClick={() => selectedSlot && !selectedSlot.isComingSoon && onConfirm(selectedSlot.id)}
            disabled={!canBegin}
            style={{
              background: canBegin ? '#4CAF50' : '#3a3d45',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '8px 20px',
              fontFamily: 'monospace',
              fontSize: 13,
              fontWeight: 'bold',
              cursor: canBegin ? 'pointer' : 'default',
              opacity: canBegin ? 1 : 0.6,
            }}
          >
            Begin Adventure
          </button>
        </div>
      </div>
    </Modal>
  );
};
